import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as ReactRouterLink } from 'react-router-dom';
import { Button, Flex } from '@chakra-ui/react';
import { ReviewsIcon } from '@udacity/chakra-uds-icons';
import dayjs from 'dayjs';

import ListItem from 'app/queue/components/list-item';
import RaisedGlyph from 'app/queue/components/raised-glyph';
import { ApiSubmission } from 'app/types/reviews';
import { getSubmissionPriceDisplay } from 'app/helpers/submission-helper';
import { LanguageTag } from 'app/common/components/language-tag/language-tag';
import { ColorTag } from 'app/common/components/color-tag/color-tag';

import styles from './assigned-submission-list-item.module.scss';

const expirations = {
  WARNING: 60, // Warn when an assignment has <= 60 minutes left.
};

type AssignedSubmissionListItemProps = { submission: ApiSubmission };

export const AssignedSubmissionListItem: React.FC<AssignedSubmissionListItemProps> = ({ submission }) => {
  const { t } = useTranslation();

  const isExpiring = (expiresAt: string | null) => {
    return dayjs(expiresAt).diff(dayjs(), 'minutes') <= expirations.WARNING;
  };

  const isExpired = useMemo(() => {
    return dayjs().isAfter(dayjs(submission.expires_at));
  }, [submission.expires_at]);

  const { base, bonus, total } = getSubmissionPriceDisplay(submission);

  const expiredStyles = isExpired ? styles['expired-submission'] : '';
  const completeIn = isExpired ? t('common.expired') : dayjs(submission.expires_at).fromNow(true);

  return (
    <ListItem className={`${styles['assigned-submission-list-item']} ${expiredStyles}`}>
      <div className={styles.glyph}>
        <RaisedGlyph icon={<ReviewsIcon w={8} h={8} />} />
      </div>

      <div className={styles.title}>
        <p>{submission.project.name}</p>
      </div>
      <Flex flexDirection="column" alignItems="flex-start">
        <label>{t('table.columns.language')}</label>
        <LanguageTag languageCode={submission.language} />
      </Flex>
      <div className={isExpiring(submission.expires_at) ? styles['complete-in-expiring'] : ''}>
        <label>{t('queue.reassignedIn')}</label>
        <p>{completeIn}</p>
      </div>

      <div className={styles['cell-group']}>
        <Flex flexDirection="column" alignItems="flex-start">
          <label>{t('common.base')}</label>
          {base ? <ColorTag variant="green">{base}</ColorTag> : '-'}
        </Flex>
        <Flex flexDirection="column" alignItems="flex-start">
          <label>{t('table.columns.bonus')}</label>
          {bonus ? <ColorTag variant="purple">{bonus}</ColorTag> : '-'}
        </Flex>
        <Flex flexDirection="column" alignItems="flex-start">
          <label>{t('table.columns.total')}</label>
          <ColorTag variant="green-dark">{total}</ColorTag>
        </Flex>
      </div>

      <div className={styles['actions']}>
        {!isExpired && (
          <Button as={ReactRouterLink} to={`/project-reviews/${submission.id}/feedback`} variant="outline" size="sm">
            {t('common.review')}
          </Button>
        )}
      </div>
    </ListItem>
  );
};
